import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://www.jinbangshengxuewc.com';

interface SharePanelProps {
  title?: string;
  description?: string;
  trigger?: 'button' | 'icon';
  className?: string;
}

export default function SharePanel({
  title = '金榜旺财 - 城乡学生互助升学平台',
  description = '城乡学生互助，AI智能匹配学伴，高考考研视频课程，助力每位学生金榜题名！',
  trigger = 'button',
  className = '',
}: SharePanelProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const shareUrl = `${BASE_URL}${location.pathname}`;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers / WeChat
      try {
        const el = document.createElement('textarea');
        el.value = shareUrl;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopied(true);
        setCopyError(false);
        setTimeout(() => setCopied(false), 2500);
      } catch {
        setCopyError(true);
        setTimeout(() => setCopyError(false), 3000);
      }
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url: shareUrl });
      } catch {
        // user cancelled
      }
    }
  };

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const shareItems = [
    {
      id: 'copy',
      icon: copied ? 'ri-check-line' : 'ri-links-line',
      label: copied ? '已复制！' : '复制链接',
      color: copied ? 'bg-green-500' : 'bg-gray-700',
      action: handleCopy,
    },
    ...(hasNativeShare
      ? [
          {
            id: 'native',
            icon: 'ri-share-forward-line',
            label: '系统分享',
            color: 'bg-amber-500',
            action: handleNativeShare,
          },
        ]
      : []),
    {
      id: 'wechat',
      icon: 'ri-wechat-line',
      label: '微信好友',
      color: 'bg-green-600',
      action: handleCopy, // copy link then user pastes in WeChat
    },
    {
      id: 'weibo',
      icon: 'ri-weibo-line',
      label: '微博',
      color: 'bg-red-500',
      action: () => {
        window.open(
          `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`,
          '_blank',
          'noopener,noreferrer'
        );
      },
    },
    {
      id: 'qq',
      icon: 'ri-qq-line',
      label: 'QQ',
      color: 'bg-sky-500',
      action: () => {
        window.open(
          `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}&desc=${encodeURIComponent(description)}`,
          '_blank',
          'noopener,noreferrer'
        );
      },
    },
  ];

  return (
    <div ref={panelRef} className={`relative ${className}`}>
      {/* Trigger */}
      {trigger === 'button' ? (
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-forward-line text-base"></i>
          分享给朋友
        </button>
      ) : (
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all cursor-pointer"
          title="分享"
        >
          <i className="ri-share-forward-line text-lg"></i>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="absolute bottom-full mb-3 right-0 w-72 bg-white rounded-2xl overflow-hidden z-50"
          style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-bold text-gray-800 text-sm">分享给朋友</span>
            <button
              onClick={() => setOpen(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>

          {/* URL preview */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                  alt="金榜旺财"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">{title}</p>
                <p className="text-xs text-gray-400 truncate">{shareUrl}</p>
              </div>
            </div>
          </div>

          {/* Share options */}
          <div className="p-4 grid grid-cols-4 gap-3">
            {shareItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  item.action();
                  if (item.id !== 'copy') setOpen(false);
                }}
                className="flex flex-col items-center gap-1.5 cursor-pointer group"
              >
                <div className={`w-11 h-11 rounded-2xl ${item.color} flex items-center justify-center transition-transform group-hover:scale-110 group-active:scale-95`}>
                  <i className={`${item.icon} text-white text-lg`}></i>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">{item.label}</span>
              </button>
            ))}
          </div>

          {/* WeChat tip */}
          <div className="px-4 pb-4">
            <div className="bg-green-50 rounded-xl px-3 py-2 flex items-start gap-2">
              <i className="ri-wechat-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
              <p className="text-xs text-green-700 leading-relaxed">
                微信分享：点击"复制链接"后，打开微信粘贴给好友即可
              </p>
            </div>
          </div>

          {/* Copy error fallback */}
          {copyError && (
            <div className="px-4 pb-3">
              <div className="bg-amber-50 rounded-xl px-3 py-2">
                <p className="text-xs text-amber-700 font-medium mb-1">请手动复制以下链接：</p>
                <p className="text-xs text-amber-600 break-all select-all">{shareUrl}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
